import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ActivationService} from 'src/app/services/activation.service';
import {
  ActivationPageState,
  CTATypes,
  ErrorCode,
  Subscription,
} from '../../model/common.enum';
import { combineLatest } from "rxjs";
import {HttpErrorResponse} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {AccountSelectionService} from 'src/app/services/account-selection.service';
import {RchAnalyticsService} from 'src/app/services/rch-analytics.service';
import {UtilService} from 'src/app/services/util.service';
import {StorageService} from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Account } from 'src/app/model/accounts.model';
import { RouteConstants } from 'src/app/constants/route-constants';

@Component({
  selector: 'rch-activation',
  templateUrl: './activation.component.html',
  styleUrls: ['./activation.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ActivationComponent implements OnInit, AfterViewInit {
  CTATypes = CTATypes;
  code: string = '';
  pageState = ActivationPageState;
  currentPageState = ActivationPageState.ENTER_CODE;
  isCodeInvalid = false;
  isLoading = false;
  errorCopyHeader = '';
  errorCopyDescription = '';
  accountsSubscriptions: Account[] | undefined;
  accountsList: Account[] | undefined;
  selectedAccountNumber: string = '';
  selectedAccountDetails: Account | undefined;
  errorCodes = ErrorCode;
  errorScenario = ErrorCode.TECH_ERROR;
  formGroup!: FormGroup;
  @ViewChild("codeInput") codeInput: ElementRef | undefined;

  constructor(
    private activationService: ActivationService,
    private translateService: TranslateService,
    private accountSelectionService: AccountSelectionService,
    private analyticsService: RchAnalyticsService,
    private utilService: UtilService,
    private storageService: StorageService
  ) {
  }

  ngOnInit(): void {
    this.initForm();
    this.getAccountSubscriptions();
    this.translateService.onLangChange.subscribe(lang => {
      this.setAriaForCodeInputs();
    })
  }

  ngAfterViewInit() {
    this.setAriaForCodeInputs();
  }

  private initForm(): void {
    this.formGroup = new FormGroup({
      codeInput: new FormControl(
        { value: this.code, disabled: false },
        Validators.required
      ),
    });
  }

  setAriaForCodeInputs() {
    const inputs = this.codeInput?.nativeElement.querySelectorAll('input');
    const labels = this.codeInput?.nativeElement.querySelectorAll('label');

    labels.forEach( label => {
      label.ariaHidden = true;
    });

    inputs.forEach(input => {
      if (input) {
        this.translateService.get('global.activation.codeInput')
          .subscribe((translated: string) => {
            input.ariaLabel = this.translateService.instant('global.activation.codeInput');
          });
      }
    });
  }

  getAccountSubscriptions() {
    this.isLoading = true;
    combineLatest([this.accountSelectionService.getAccountSubscriptions(), this.accountSelectionService.getAccountList()])
    .subscribe({
      next: ([accountSubscriptions, accountList]) => {
        this.accountsList = accountList.content.accounts;
        const activeAccounts = this.filterActiveAccounts(accountSubscriptions.content.accounts, this.accountsList);
        this.applyIPTVFilter(activeAccounts);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 400 || error.status === 401) {
          this.utilService.goToSignIn();
        }
        this.handleError(error.status.toString() as ErrorCode)
      },
      complete: () => (this.isLoading = false),
    });
  }

  handleActivateClick() {
    console.log("#details", this.selectedAccountDetails);
    this.isCodeInvalid = false;
    console.log("code", this.code);
    
    if (!this.isCodeValid()) {
      this.isCodeInvalid = true;
      return;
    }
    this.isLoading = true;
    this.activationService.activate(this.code, this.selectedAccountNumber, this.selectedAccountDetails?.cbpidDetails[0]?.cbpid).subscribe({
      next: (v) => {
        this.currentPageState = ActivationPageState.SUCCESS;
      },
      error: (error: HttpErrorResponse) => {
        this.analyticsService.rchTrackError(error);
        this.handleError(error?.status?.toString() as ErrorCode)
      },
      complete: () => (this.isLoading = false),
    });
  }

  private isCodeValid() {
    return !(
      [null, undefined, ''].includes(this.code) || this.code?.length < 6
    );
  }

  private handleError(error: ErrorCode) {
    // checks if the error is present on ErrorCodes enum. If you are introducing a new error scenario, make sure you added the error to the enum
    if (!Object.values(ErrorCode).includes(error)) {
      const defaultError = ErrorCode.TECH_ERROR;
      error = defaultError;
    }
    this.errorScenario = error;
    /* Commenting this out, and moved it to HTML.
    this.errorCopyHeader = this.translateService.instant(
      `global.activation.errorCodes.${[error]}.header`
    );
    this.errorCopyDescription = this.translateService.instant(
      `global.activation.errorCodes.${[error]}.description`
    );*/
    this.isLoading = false;
    this.currentPageState = ActivationPageState.ERROR;
  }

  handleCodeChanged(e: string) {
    this.code = e;
  }

  handleTryAgain() {
    this.code = '';
    this.currentPageState = ActivationPageState.ENTER_CODE;
  }

  handleAccountSelection(selectedAccount: any) {
    console.log("Activation component - handleAccountSelection() - Selected account: ", selectedAccount);
    this.selectedAccountNumber = selectedAccount.accountNumber.toString();
    if(this.selectedAccountNumber && selectedAccount?.accountAlias){
      this.getSelectedAccountDetails(selectedAccount.accountAlias,  this.getAccountTypeNumber(this.selectedAccountNumber));
    }
    this.currentPageState = ActivationPageState.ENTER_CODE;
  }

  private getAccountTypeNumber(selectedAccountNumber: string) {
    return this.accountsList?.find(account => account.accountNumber === selectedAccountNumber)?.accountTypeNumber
  }

  private filterActiveAccounts(accountsSubscriptions: Account[], accountsList: Account[]): Account[] {
    const activeAccountNumbers = new Set(
      accountsList
        .filter(account => account.status.toUpperCase() === "ACTIVE")
        .map(account => account.accountNumber),
    );
    return accountsSubscriptions.filter(account => activeAccountNumbers.has(account.accountNumber));
  }

  private applyIPTVFilter(activeAccounts: Account[]) {
    if (!activeAccounts.length) {
      this.handleError(ErrorCode.SUBSCRIBERS_ONLY);
      return;
    }

    console.log("Activation component - applyIPTVFilter() - Active accounts before IPTV filter: ", activeAccounts);
    this.accountsSubscriptions = activeAccounts.filter((account: Account) => {
      return (
        account.subscriptions.filter((element: any) => {
          return (
            environment.subType.includes(element.subType) &&
            element.status === Subscription.status
          );
        }).length > 0
      );
    });

    if (!this.accountsSubscriptions.length) {
      this.handleError(ErrorCode.SUBSCRIBERS_ONLY);
      return;
    }

    if (this.accountsSubscriptions.length === 1) {
      this.handleAccountSelection(this.accountsSubscriptions[0]);
    } else {
      this.currentPageState = ActivationPageState.ACCOUNTSELECTION;
    }
    this.analyticsService.rchTrackPageLoad();
    console.log("Activation component - applyIPTVFilter() - Accounts after IPTV filter: ", this.accountsSubscriptions);
  }

  private getSelectedAccountDetails(accountAlias, accountTypeNumber?: string){
    this.accountSelectionService.getAccountDetails(accountAlias, accountTypeNumber).subscribe(res => {
      this.selectedAccountDetails = res.content.account;
    });
  }

  public redirectToExternalSupport() {
    window.open(RouteConstants.external.support, "_blank");
  }
}
